export type ChartAmountUnit = 'rial' | 'toman';

export interface FormatChartAmountOptions {
  /** Target currency display unit. Defaults to 'rial'. */
  readonly unit?: ChartAmountUnit | undefined;
  /** Whether to append the Persian currency name (' ریال' or ' تومان'). Defaults to false. */
  readonly includeSuffix?: boolean | undefined;
}

/** fa-IR grouping for a plain count (rounds — never used for money). */
export function formatCountFa(value: number): string {
  return Math.round(value).toLocaleString('fa-IR');
}

/**
 * Central money amount formatter for ChartKit (axes, tooltips, KPI badges/cards).
 * Consumes raw integer Rial values (as string, number, or bigint).
 * Divides by 10 ONLY when unit is 'toman', preserving integer Rial in data.
 * Charts and KPI tiles never perform manual `/ 10` divisions themselves.
 *
 * @param value Raw integer Rial amount
 * @param unitOrOptions 'rial' | 'toman' or options object with unit and includeSuffix
 */
export function formatChartAmount(
  value: number | string | bigint | null | undefined,
  unitOrOptions: ChartAmountUnit | FormatChartAmountOptions = 'rial',
): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  let numericValue: number;
  if (typeof value === 'bigint') {
    numericValue = Number(value);
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return '—';
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return '—';
    }
    numericValue = parsed;
  } else if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      return '—';
    }
    numericValue = value;
  } else {
    return '—';
  }

  const sign = numericValue < 0 ? '-' : '';
  const absValue = Math.abs(numericValue);

  const options: FormatChartAmountOptions =
    typeof unitOrOptions === 'string' ? { unit: unitOrOptions } : unitOrOptions;
  const unit = options.unit ?? 'rial';
  const includeSuffix = options.includeSuffix ?? false;

  const displayValue = unit === 'toman' ? Math.round(absValue / 10) : Math.round(absValue);
  const formattedDigits = displayValue.toLocaleString('fa-IR');
  const suffix = includeSuffix ? (unit === 'toman' ? ' تومان' : ' ریال') : '';

  return `${sign}${formattedDigits}${suffix}`;
}

/**
 * Backward-compatible helper for integer Rial display formatting.
 * Equivalent to formatChartAmount(value, 'rial').
 */
export function formatRialFa(value: number | string | bigint): string {
  return formatChartAmount(value, 'rial');
}
